import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Maximize2, Minimize2, Pause, Play, Volume2, VolumeX } from "lucide-react";
import { Container, Reveal } from "@/components/common/Primitives";
import { VideoLayer } from "@/components/home/HeroKit";
import { useLang } from "@/lib/i18n";

/**
 * The hotel's own film (2:08), shown whole. Idle: a silent 15-second teaser loop with a play mark.
 * Playing: the full film with sound and a small custom control bar (play/pause, time, seek bar with
 * chapter ticks, mute, fullscreen) that hides itself while you watch. Chapters under the frame seek.
 */
export const FILM = {
  src1080: "/video/skanevik-film-1080.mp4",
  src720: "/video/skanevik-film-720.mp4",
  webm720: "/video/skanevik-film-720.webm",
  poster: "/video/film-poster.webp",
  teaser: { src: "/video/hero-1080.mp4", webm: "/video/hero-1080.webm", poster: "/video/hero-poster.webp" },
  duration: 128,
  chapters: [
    { t: 0, nn: "Fjorden", en: "The fjord" },
    { t: 8, nn: "Hotellet", en: "The hotel" },
    { t: 22, nn: "Frukost", en: "Breakfast" },
    { t: 52, nn: "Romma", en: "The rooms" },
    { t: 90, nn: "Restauranten", en: "The restaurant" },
    { t: 98, nn: "Terrassen", en: "The terrace" },
    { t: 116, nn: "Fjorden att", en: "Back to the fjord" },
  ],
};

const fmt = (s) => {
  const n = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};

export const FilmSection = ({ testid = "home-film" }) => {
  const { tx, lang } = useLang();
  const reduce = useReducedMotion();
  const frame = useRef(null);
  const vid = useRef(null);
  const hideTimer = useRef(null);
  const touch = useRef(false); // on touch screens the first tap only reveals the controls
  const [mode, setMode] = useState("idle"); // idle | playing
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(FILM.duration);
  const [controls, setControls] = useState(true);
  const [fs, setFs] = useState(false);
  const [pendingSeek, setPendingSeek] = useState(null);
  // mount the silent teaser only once the frame is near the viewport (it is a 15 s mp4/webm)
  const near = useInView(frame, { once: true, margin: "600px 0px 600px 0px" });

  const pickSrc = () => (window.matchMedia && window.matchMedia("(min-width: 1024px)").matches ? FILM.src1080 : FILM.src720);
  const [src, setSrc] = useState(FILM.src720);

  const start = useCallback((at = 0) => {
    setSrc(pickSrc());
    setPendingSeek(at);
    setMode("playing");
  }, []);

  // Once the real <video> is mounted, seek (if asked) and play.
  useEffect(() => {
    if (mode !== "playing") return undefined;
    const v = vid.current;
    if (!v) return undefined;
    const onLoaded = () => {
      setDuration(v.duration || FILM.duration);
      if (pendingSeek) v.currentTime = pendingSeek;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };
    v.addEventListener("loadedmetadata", onLoaded, { once: true });
    v.load();
    return () => v.removeEventListener("loadedmetadata", onLoaded);
  }, [mode, src]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onFs = () => setFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const poke = () => {
    setControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setControls(false), 2600);
  };
  useEffect(() => () => hideTimer.current && clearTimeout(hideTimer.current), []);

  const toggle = () => {
    const v = vid.current;
    if (!v) return;
    if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } else v.pause();
    poke();
  };
  const seek = (t) => {
    const v = vid.current;
    if (!v) return;
    v.currentTime = Math.min(Math.max(0, t), duration);
    setTime(v.currentTime);
    poke();
  };
  const seekChapter = (t) => (mode === "playing" ? (seek(t), vid.current?.paused && toggle()) : start(t));
  const toggleMute = () => { const v = vid.current; if (!v) return; v.muted = !v.muted; setMuted(v.muted); poke(); };
  const toggleFs = () => {
    const el = frame.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.();
    poke();
  };
  const onKey = (e) => {
    // buttons and the seek bar handle their own keys
    if (e.target !== frame.current) return;
    if (mode !== "playing") { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); start(0); } return; }
    if (e.key === " " || e.key.toLowerCase() === "k") { e.preventDefault(); toggle(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); seek(time + 5); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); seek(time - 5); }
    else if (e.key.toLowerCase() === "m") toggleMute();
    else if (e.key.toLowerCase() === "f") toggleFs();
  };

  const pct = duration ? (time / duration) * 100 : 0;
  const activeChapter = FILM.chapters.reduce((acc, c, i) => (time >= c.t ? i : acc), 0);
  const label = (c) => (lang === "en" ? c.en : c.nn);

  return (
    <section className="mt-20 bg-[hsl(214,49%,10%)] py-16 text-white lg:mt-32 lg:py-24" data-testid={testid} data-mode={mode}>
      <Container>
        <Reveal>
          <div className="grid grid-cols-12 items-end gap-x-8 gap-y-5">
            <div className="col-span-12 lg:col-span-7">
              <p className="flex items-center gap-3 text-sm font-semibold text-white/60 before:h-px before:w-8 before:bg-white/30">{tx("Filmen", "The film")}</p>
              <h2 className="mt-4 text-[2.125rem] font-semibold leading-[0.98] tracking-[-0.035em] text-white sm:text-[2.75rem] lg:text-[3.25rem]" data-testid={`${testid}-title`}>{tx("Skånevik på to minutt.", "Skånevik in two minutes.")}</h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:pb-2">
              <p className="max-w-[34rem] text-[1.0625rem] leading-relaxed text-white/70 lg:text-lg">{tx("Over fjorden med drone, inn gjennom resepsjonen, forbi kjøkkenet og ut på terrassen. Heile filmen om hotellet, med lyd.", "Over the fjord by drone, in through reception, past the kitchen and out onto the terrace. The whole film about the hotel, with sound.")}</p>
              <button type="button" onClick={() => (mode === "playing" ? toggle() : start(0))} className="mt-5 inline-flex items-center gap-2.5 text-[0.9375rem] font-semibold text-white underline decoration-white/30 underline-offset-[6px] transition-colors hover:decoration-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70" data-testid={`${testid}-header-play`}>
                <Play className="h-4 w-4 fill-current" /> {mode === "playing" ? (playing ? tx("Pause", "Pause") : tx("Hald fram", "Resume")) : tx("Spel av filmen", "Play the film")} <span className="font-normal text-white/50 tabular">· 2:08</span>
              </button>
            </div>
          </div>
        </Reveal>
      </Container>

      <div className="mx-auto w-full max-w-[1680px] px-0 sm:px-8 lg:px-10">
        <Reveal delay={0.08}>
          <div
            ref={frame}
            tabIndex={0}
            role="region"
            aria-label={tx("Filmen om Skånevik Fjordhotel", "The film about Skånevik Fjordhotel")}
            onKeyDown={onKey}
            onMouseMove={mode === "playing" ? poke : undefined}
            onMouseLeave={mode === "playing" ? () => setControls(false) : undefined}
            className={`group/film relative mt-8 aspect-video w-full overflow-hidden bg-black ring-1 ring-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 lg:mt-12 ${fs ? "rounded-none" : "rounded-none sm:rounded-lg lg:rounded-2xl"} ${mode === "playing" && !controls ? "cursor-none" : ""}`}
            data-testid={`${testid}-frame`}
          >
            {mode === "idle" ? (
              <>
                {near ? (
                  <VideoLayer {...FILM.teaser} parallaxRef={frame} parallax={0} testid={`${testid}-teaser`} />
                ) : (
                  <img src={FILM.teaser.poster} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" decoding="async" />
                )}
                <div className="scrim-hero-soft pointer-events-none absolute inset-x-0 bottom-0 h-1/2" aria-hidden />
                <button
                  type="button"
                  onClick={() => start(0)}
                  className="group/play absolute inset-0 flex items-center justify-center focus-visible:outline-none"
                  aria-label={tx("Spel av filmen", "Play the film")}
                  data-testid={`${testid}-play`}
                >
                  <motion.span
                    initial={reduce ? false : { scale: 0.9, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
                    className="flex h-20 w-20 items-center justify-center rounded-full border border-white/50 bg-white/10 text-white backdrop-blur-md transition-[background-color,color,transform] duration-200 group-hover/play:bg-white group-hover/play:text-foreground group-active/play:scale-95 lg:h-24 lg:w-24"
                  >
                    <Play className="h-7 w-7 translate-x-0.5 fill-current lg:h-8 lg:w-8" />
                  </motion.span>
                </button>
                <div className="pointer-events-none absolute inset-x-5 bottom-5 flex items-end justify-between gap-4 lg:inset-x-7 lg:bottom-7">
                  <p className="hero-shadow">
                    <span className="block text-[0.9375rem] font-semibold lg:text-base">{tx("Spel av filmen", "Play the film")}</span>
                    <span className="block text-sm text-white/70">{tx("Med lyd · 2:08", "With sound · 2:08")}</span>
                  </p>
                </div>
              </>
            ) : (
              <>
                <video
                  ref={vid}
                  key={src}
                  poster={FILM.poster}
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 h-full w-full bg-black"
                  onPointerDown={(e) => { touch.current = e.pointerType === "touch"; }}
                  onClick={() => { if (touch.current && playing && !controls) { poke(); return; } toggle(); }}
                  onPlay={() => { setPlaying(true); poke(); }}
                  onPause={() => { setPlaying(false); setControls(true); }}
                  onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
                  onDurationChange={(e) => setDuration(e.currentTarget.duration || FILM.duration)}
                  onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
                  onEnded={() => { setMode("idle"); setTime(0); setPlaying(false); }}
                  data-testid={`${testid}-video`}
                >
                  {/* H.264 first (Safari/iOS, Chrome, Edge), VP9 WebM for browsers without H.264 */}
                  <source src={src} type="video/mp4" />
                  <source src={FILM.webm720} type="video/webm" />
                  <track kind="captions" />
                </video>

                {/* Big centred pause mark – appears only when paused */}
                {!playing && (
                  <button type="button" onClick={toggle} className="absolute inset-0 flex items-center justify-center bg-black/20 focus-visible:outline-none" aria-label={tx("Spel av", "Play")} data-testid={`${testid}-resume`}>
                    <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white text-foreground shadow-pop lg:h-24 lg:w-24"><Play className="h-7 w-7 translate-x-0.5 fill-current lg:h-8 lg:w-8" /></span>
                  </button>
                )}

                {/* Control bar */}
                <div className={`absolute inset-x-0 bottom-0 transition-opacity duration-300 ${controls || !playing ? "opacity-100" : "opacity-0"}`} data-testid={`${testid}-controls`}>
                  <div className="scrim-hero-soft pointer-events-none absolute inset-x-0 bottom-0 h-40" aria-hidden />
                  <div className="relative px-4 pb-3 pt-6 lg:px-6 lg:pb-4">
                    <div className="relative">
                      <input
                        type="range"
                        min={0}
                        max={duration || FILM.duration}
                        step={0.1}
                        value={time}
                        onChange={(e) => seek(Number(e.target.value))}
                        className="film-range relative z-[2] w-full"
                        style={{ "--p": `${pct}%` }}
                        aria-label={tx("Spol", "Seek")}
                        aria-valuetext={`${fmt(time)} / ${fmt(duration)}`}
                        data-testid={`${testid}-seek`}
                      />
                      {/* chapter ticks */}
                      <div className="pointer-events-none absolute inset-x-0 top-1/2 z-[1] h-[3px] -translate-y-1/2" aria-hidden>
                        {FILM.chapters.slice(1).map((c) => (
                          <span key={c.t} className="absolute top-0 h-full w-px bg-[hsl(214,49%,10%)]" style={{ left: `${(c.t / (duration || FILM.duration)) * 100}%` }} />
                        ))}
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={toggle} className="inline-flex h-10 w-10 items-center justify-center rounded-md text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70" aria-label={playing ? tx("Pause", "Pause") : tx("Spel av", "Play")} data-testid={`${testid}-toggle`}>
                          {playing ? <Pause className="h-5 w-5 fill-current" /> : <Play className="h-5 w-5 translate-x-px fill-current" />}
                        </button>
                        <button type="button" onClick={toggleMute} className="inline-flex h-10 w-10 items-center justify-center rounded-md text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70" aria-label={muted ? tx("Lyd på", "Unmute") : tx("Lyd av", "Mute")} data-testid={`${testid}-mute`}>
                          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                        </button>
                        <p className="ml-2 text-sm tabular text-white/85" data-testid={`${testid}-time`}>{fmt(time)} <span className="text-white/45">/ {fmt(duration)}</span></p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="hidden text-sm text-white/70 sm:block" data-testid={`${testid}-chapter-label`}>{label(FILM.chapters[activeChapter])}</p>
                        <button type="button" onClick={toggleFs} className="inline-flex h-10 w-10 items-center justify-center rounded-md text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70" aria-label={fs ? tx("Avslutt fullskjerm", "Exit full screen") : tx("Fullskjerm", "Full screen")} data-testid={`${testid}-fullscreen`}>
                          {fs ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </Reveal>

        {/* Chapters – a stepped timeline: the line above each beat fills as the film passes it */}
        <Reveal delay={0.14}>
          <ol className="no-scrollbar mt-6 flex gap-5 overflow-x-auto px-5 sm:px-0 lg:mt-8 lg:gap-8" aria-label={tx("Kapittel", "Chapters")} data-testid={`${testid}-chapters`}>
            {FILM.chapters.map((c, i) => {
              const active = mode === "playing" && i === activeChapter;
              const passed = mode === "playing" && i < activeChapter;
              return (
                <li key={c.t} className="min-w-[132px] flex-1 shrink-0 sm:min-w-0">
                  <button
                    type="button"
                    onClick={() => seekChapter(c.t)}
                    className="group/ch block w-full border-t pt-3 text-left transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 data-[state=active]:border-white data-[state=passed]:border-white/70 data-[state=idle]:border-white/20 data-[state=idle]:hover:border-white/60"
                    data-state={active ? "active" : passed ? "passed" : "idle"}
                    aria-current={active ? "true" : undefined}
                    data-testid={`${testid}-chapter-${i}`}
                  >
                    <span className={`block text-xs tabular transition-colors ${active ? "text-white" : "text-white/45 group-hover/ch:text-white/70"}`}>{fmt(c.t)}</span>
                    <span className={`mt-1 block text-[0.9375rem] font-semibold leading-snug transition-colors ${active ? "text-white" : "text-white/70 group-hover/ch:text-white"}`}>{label(c)}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </Reveal>
      </div>
    </section>
  );
};
